
import { Props, TabConfig, SECTION_TYPE } from './form.model';
import { DataTableSchema, DefaultTableSchema, TITLE_TYPE } from './dynamic-data-table.model';
import { DynamicDataTableComponent } from '../layout/dynamic-data-table/dynamic-data-table.component';
import { FETCH_METHODS, SearchConfig } from './api.model';
import { FieldNames } from './form-fields.model';

export const getQueryDatesForTurnOverReport = (date: 'start' | 'end') => {
  const today = new Date();

  // End month: two months ago if day < 5, else previous month
  const monthsBack = today.getDate() < 5 ? 2 : 1;

  // Last day of target end month
  const endDate = new Date(today.getFullYear(), today.getMonth() - monthsBack + 1, 0);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (date === 'end') {
    return `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`;
  }

  // Start date = (end date) - 12 months + 1 day
  const startMinus12 = new Date(endDate.getFullYear(), endDate.getMonth() - 12, endDate.getDate());
  const startDate = new Date(
    startMinus12.getFullYear(),
    startMinus12.getMonth(),
    startMinus12.getDate() + 1
  );

  return `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`;
}

export const DbFieldKeys = {
  AreaName: 'areaName',
  StartPeriod: 'startPeriod',
  District: 'district',
  DealerName: 'dealerName',
  JobChange: 'jobChange',
  JobChangePercent: 'jobChangePercent',
  DealerChange: 'dealerChange',
  DealerChangePercent: 'dealerChangePercent',
  AreaChange: 'areaChange',
  ChangedArea: 'changedArea',
  AreaChangePercent: 'areaChangePercent',
  TotalAdded: 'totalAdded',
  TotalAddedPercent: 'totalAddedPercent',
  TotalLeaving: 'totalLeaving',
  TotalLeavingPercent: 'totalLeavingPercent',
  Terminated: 'terminated',
  TerminatedPercent: 'terminatedPercent',
  EndPeriod: 'endPeriod'
}
export const Skip = "skip";
export const CblHighlightRow = "CblHighlightRow";
const Labels = {
  Area:"Area",
  District: "District",
  DistrictHash: "District#",
  DistrictPercent: "District%",
  ChangedArea:'Changed Area',
  AreaChange:'Area Change',
  AreaHash:'Area#',
  AreaPercent:'Area%',
  AreaChangePercent:'Area Change %',
  TotalLeaving : "Total Leaving",
  TotalLeavingPercent: "Total Leaving %",
  Terminated: 'Terminated',
  TerminatedPercent: 'Terminated%',
  TerminatedHash: 'Terminated#',
  TerminatedDlr: 'Terminated Dlr#',
  TerminatedDlrPercent: 'Terminated Dlr %',
  ChangedDistrict: 'Changed District',
  StartPeriodHash: "Start Period#",
  StartPeriod: "Start Period",
  TotalAdded : "Total Added",
  TotalAddedPercent: "Total Added %",
  TotalLeavingDlrHash : "Total Leaving Dlr#",
  JobChange:"Job Change",
  EndPeriod:"End Period",
  EndPeriodHash:"End Period#",
  DealerChange: "Dealer Change",
  DealerChangeHash: "Dealer Change#",
  DealerChangePercent: "Dealer Change%",
  DealerHash: "Dealer#",
  DealerPercent: "Dealer%",
  TotalLeavingDlrPercent : "Total Leaving Dlr%",
  TotalAddedDlr: 'Total Added Dlr#',
  TotalAddedDlrPercent: 'Total Added Dlr %',
  JobChangePercent: 'Job Change %',
  TurnoverreportData:"Turnover report Data",
  TotalLeavingJobRoleTerm : "Total Leaving - Job Role CHANGE + Term",
  JobTitleChangeHash:"Job Title Change#",
  JobTitleChangePercent:"Job Title Change%",
  ChangedAreaHash:'Changed Area#',
  TotalLeavingAreaHash : "Total Leaving Area#",
  TotalLeavingAreaPercent : "Total Leaving Area%",

}

export const TurnOverReportDataCalculationConfig: Record<string, string> = {
  [DbFieldKeys['JobChange']]: DbFieldKeys['JobChangePercent'],
  [DbFieldKeys['DealerChange']]: DbFieldKeys['DealerChangePercent'],
  [DbFieldKeys['TotalAdded']]: DbFieldKeys['TotalAddedPercent'],
  [DbFieldKeys['TotalLeaving']]: DbFieldKeys['TotalLeavingPercent'],
  [DbFieldKeys['Terminated']]: DbFieldKeys['TerminatedPercent'],
  [DbFieldKeys['StartPeriod']]: Skip,
  [DbFieldKeys['EndPeriod']]: Skip,
  [DbFieldKeys['AreaChange']]: Skip
}

export const TurnOverReportAreaSearchConfig: SearchConfig = {
  method: FETCH_METHODS['Post'],
  path: 'turnOverReport',
  body: {
    [FieldNames['JobRole']]: "",
    [FieldNames['Area']]: "",
    [FieldNames['District']]: "",
    [FieldNames['Month']]: "",
    [FieldNames['Year']]: "",
    "reportType": "area",
    [FieldNames['DealerCode']]: "",
    "startDate": getQueryDatesForTurnOverReport('start'),
    "endDate": getQueryDatesForTurnOverReport('end')
  }
}

const AreaDescription = [
  "Start period # - Associates who are Active in their area during start of turnover period",
  "Job Title Change# - Associates who changed the job role during the turnover period",
  "Dlr to Dlr Change - Associates who changed the dealer during the turnover period",
  "Changed Area# - Associates who are Active but changed the area",
  "Total Added - Associates who joined the area. It includes new users and active users from other area(s)",
  "Total Leaving# - Associates who are terminated and changed the job role in the same area during the turnover period",
  "Terminated # - Associates who are inactive and terminated from Lexus - Associates who were in the same job role and terminated",
  "End Period # - Associates who are active in the area during the end of turnover period."
];
const DistrictDescriptions = [
  "Start Period # - Associates who are active in the district during the start of turn over period.",
  "Job Title Change - Associates who changed the job role during the turn over period.",
  "Dlr to Dlr Change - Associates who changed the dealer during the turn over period.",
  "Changed Dist # - Associates who are active but changed their district.",
  "Total Added - Associates who joined the district. It includes new users and active usres from other district(s).",
  "Total Leaving - Associates who are terminated and changed the job role in the same area during the turnover period",
  "Terminated - Associates who are inactive and terminated from Lexus. -Associates who were in the same job role and are terminated",
  "End Period # - Associates who are active in the district during the end of turn over period."
];
const DealerDescriptions = [
  "Start Period # - Associates who are active in the area during the start of turn over period.",
  "Job Title Change - Associates who changed the job role during the turn over period.",
  "Dlr to Dlr Change - Associates who changed the dealer during the turn over period.",
  "Total Added - Associates who joined the area. It includes new users and active usres from other area(s).",
  "Total Leaving - Associates who are terminated and changed the job role in the same area during the turnover period",
  "Terminated - Associates who are inactive and terminated from Lexus-Associates who were in the same job role and are terminated",
  "End Period # - Associates who are active in the area during the end of turn over period."
]

export const TurnOverTabIds = {
  area: 'Area',
  areaCertification: "Area Certification",
  nationalCertification: "National Certification",
  district: 'District',
  districtCertification: 'District Certification',
  dealer: 'Dealer',
  dealerCertification: 'Dealer Certification'
}

const ColHeaders = {
  Area: {
    label: '', fieldKey: 'harea', sub_headers: [
      { label: "Area", fieldKey: DbFieldKeys.AreaName },
      { label: Labels['StartPeriodHash'], fieldKey: DbFieldKeys.StartPeriod },
    ]
  },
  District: {
    label: '', fieldKey: 'harea', sub_headers: [
      { label: "District", fieldKey: DbFieldKeys.District },
      { label: Labels['StartPeriodHash'], fieldKey: DbFieldKeys.StartPeriod },
    ]
  },
  Dealer: {
    label: '', fieldKey: 'harea', sub_headers: [
      { label: "Dealer", fieldKey: DbFieldKeys.DealerName },
      { label: Labels['StartPeriodHash'], fieldKey: DbFieldKeys.StartPeriod },
    ]
  },
  ChangedArea: {
    label: 'Changed Area', fieldKey: 'hchangedArea', sub_headers: [
      { label: "Changed Area#", fieldKey: DbFieldKeys.AreaChange }
    ]
  },
  ChangedDistrict: {
    label: 'Changed Dist #', fieldKey: 'hchangedDistrict', sub_headers: [
      { label: "Changed Dist #", fieldKey: DbFieldKeys.AreaChange }
    ]
  },
  TotalAddedArea: {
    label: Labels['TotalAdded'], fieldKey: 'htotalAdded', sub_headers: [
      { label: "Area#", fieldKey: DbFieldKeys.TotalAdded },
      { label: "Area%", fieldKey: DbFieldKeys.TotalAddedPercent, isPercent: true }
    ]
  },
  TotalAddedDistrict: {
    label: Labels['TotalAdded'], fieldKey: 'htotalAdded', sub_headers: [
      { label: "District#", fieldKey: DbFieldKeys.TotalAdded },
      { label: "District%", fieldKey: DbFieldKeys.TotalAddedPercent, isPercent: true }
    ]
  },
  TotalAddedDealer: {
    label: Labels['TotalAdded'], fieldKey: 'htotalAdded', sub_headers: [
      { label: "Dealer#", fieldKey: DbFieldKeys.TotalAdded },
      { label: "Dealer%", fieldKey: DbFieldKeys.TotalAddedPercent, isPercent: true }
    ]
  },
  TotalLeavingRoleArea: {
    label: Labels['TotalLeavingJobRoleTerm'], fieldKey: 'totalLeavingRole', sub_headers: [
      { label: "Total Leaving Area#", fieldKey: DbFieldKeys.TotalLeaving },
      { label: "Total Leaving Area%", fieldKey: DbFieldKeys.TotalLeavingPercent, isPercent: true }
    ]
  },
  TotalLeavingRoleDistrict: {
    label: Labels['TotalLeavingJobRoleTerm'], fieldKey: 'totalLeavingRole', sub_headers: [
      { label: "Total Leaving District#", fieldKey: DbFieldKeys.TotalLeaving },
      { label: "Total Leaving District%", fieldKey: DbFieldKeys.TotalLeavingPercent, isPercent: true }
    ]
  },
  TotalLeavingRoleDealer: {
    label: Labels['TotalLeavingJobRoleTerm'], fieldKey: 'totalLeavingRole', sub_headers: [
      { label: Labels['TotalLeavingDlrHash'], fieldKey: DbFieldKeys.TotalLeaving },
      { label: Labels['TotalLeavingDlrPercent'], fieldKey: DbFieldKeys.TotalLeavingPercent, isPercent: true }
    ]
  },
  JobTitleChange: {
    label: 'Job Title Change', fieldKey: 'JobTitleChange', sub_headers: [
      { label: "Job Title Change#", fieldKey: DbFieldKeys.JobChange },
      { label: "Job Title Change%", fieldKey: DbFieldKeys.JobChangePercent, isPercent: true },
    ]
  },
  DlrtoDlr: {
    label: 'Dlr to Dlr Change', fieldKey: 'DlrtoDlr', sub_headers: [
      { label: "Dealer Change#", fieldKey: DbFieldKeys.DealerChange },
      { label: "Dealer Change%", fieldKey: DbFieldKeys.DealerChangePercent, isPercent: true },
    ]
  },

  Terminated: {
    label: 'Terminated - Job Role SAME + Term', fieldKey: 'Terminated', sub_headers: [
      { label: "Terminated#", fieldKey: DbFieldKeys.Terminated },
      { label: "Terminated%", fieldKey: DbFieldKeys.TerminatedPercent, isPercent: true }
    ]
  },
  EndPeriod: {
    label: '', fieldKey: 'hendPeriod', sub_headers: [
      { label: "End Period#", fieldKey: DbFieldKeys.EndPeriod }
    ]
  }
}

const TurnOverConfigs = {
  [TurnOverTabIds['area']]: {
    exportSchema: {
      fileName: 'Turn_Over',
      descriptions: AreaDescription,
      schema: {
        [DbFieldKeys['AreaName']]: Labels['Area'],
        [DbFieldKeys['StartPeriod']]: Labels['StartPeriodHash'],
        [DbFieldKeys['JobChange']]: Labels['JobTitleChangeHash'],
        [DbFieldKeys['JobChangePercent']]: Labels['JobTitleChangePercent'],
        [DbFieldKeys['DealerChange']]: Labels['DealerChangeHash'],
        [DbFieldKeys['DealerChangePercent']]: Labels['DealerChangePercent'],
        [DbFieldKeys['ChangedArea']]: Labels['ChangedAreaHash'],
        [DbFieldKeys['AreaChange']]: Labels['AreaHash'],
        [DbFieldKeys['AreaChangePercent']]: Labels['AreaPercent'],
        [DbFieldKeys['TotalLeaving']]: Labels['TotalLeavingAreaHash'],
        [DbFieldKeys['TotalLeavingPercent']]: Labels['TotalLeavingAreaPercent'],
        [DbFieldKeys['Terminated']]: Labels['TerminatedHash'],
        [DbFieldKeys['TerminatedPercent']]: Labels['TerminatedPercent'],
        [DbFieldKeys['EndPeriod']]: Labels['EndPeriodHash'],
      },
      exportMessage: Labels['TurnoverreportData']
    },
    columnSchema: [
      ColHeaders['Area'], ColHeaders['JobTitleChange'], ColHeaders['DlrtoDlr'], ColHeaders['ChangedArea'],
      ColHeaders['TotalAddedArea'], ColHeaders['TotalLeavingRoleArea'], ColHeaders['Terminated'], ColHeaders['EndPeriod']
    ],
    props:{
      isSearchVisible: false,
      isTabStretch: false,
      description: AreaDescription
    }
  },
  [TurnOverTabIds['district']]: {
    exportSchema: {
      fileName: 'Turn_Over',
      descriptions: DistrictDescriptions,
      schema: {
        [DbFieldKeys['District']]: Labels['District'],
        [DbFieldKeys['StartPeriod']]: Labels['StartPeriodHash'],
        [DbFieldKeys['JobChange']]: Labels['JobTitleChangeHash'],
        [DbFieldKeys['JobChangePercent']]: Labels['JobTitleChangePercent'],
        [DbFieldKeys['DealerChange']]: Labels['DealerChangeHash'],
        [DbFieldKeys['DealerChangePercent']]: Labels['DealerChangePercent'],
        [DbFieldKeys['AreaChange']]: Labels['ChangedDistrict'],
        [DbFieldKeys['TotalAdded']]: Labels['DistrictHash'],
        [DbFieldKeys['TotalAddedPercent']]: Labels['DistrictPercent'],
        [DbFieldKeys['TotalLeaving']]: 'Total Leaving District#',
        [DbFieldKeys['TotalLeavingPercent']]: 'Total Leaving District%',
        [DbFieldKeys['Terminated']]: Labels['TerminatedHash'],
        [DbFieldKeys['TerminatedPercent']]: Labels['TerminatedPercent'],
        [DbFieldKeys['EndPeriod']]: Labels['EndPeriodHash'],
      },
      exportMessage: Labels['TurnoverreportData']
    },
    columnSchema: [
      ColHeaders['District'], ColHeaders['JobTitleChange'], ColHeaders['DlrtoDlr'], ColHeaders['ChangedDistrict'],
      ColHeaders['TotalAddedDistrict'], ColHeaders['TotalLeavingRoleDistrict'], ColHeaders['Terminated'], ColHeaders['EndPeriod'],
    ],
    props:{
      isSearchVisible: false,
      isTabStretch: false,
      description: DistrictDescriptions
    }
  },
  [TurnOverTabIds['dealer']]: {
    exportSchema: {
      fileName: 'Turn_Over',
      descriptions: DealerDescriptions,
      isCamelCase:[DbFieldKeys['DealerName']],
      schema: {
        [DbFieldKeys['DealerName']]: 'Dealer',
        [DbFieldKeys['StartPeriod']]: Labels['StartPeriodHash'],
        [DbFieldKeys['JobChange']]: Labels['JobTitleChangeHash'],
        [DbFieldKeys['JobChangePercent']]: Labels['JobTitleChangePercent'],
        [DbFieldKeys['DealerChange']]: Labels['DealerChangeHash'],
        [DbFieldKeys['DealerChangePercent']]: Labels['DealerChangePercent'],
        [DbFieldKeys['TotalAdded']]: Labels['DealerHash'],
        [DbFieldKeys['TotalAddedPercent']]: Labels['DealerPercent'],
        [DbFieldKeys['TotalLeaving']]: Labels['TotalLeavingDlrHash'],
        [DbFieldKeys['TotalLeavingPercent']]: Labels['TotalLeavingDlrPercent'],
        [DbFieldKeys['Terminated']]: Labels['TerminatedHash'],
        [DbFieldKeys['TerminatedPercent']]: Labels['TerminatedPercent'],
        [DbFieldKeys['EndPeriod']]: Labels['EndPeriodHash'],
      },
      exportMessage: Labels['TurnoverreportData']
    },
    columnSchema: [
      ColHeaders['Dealer'], ColHeaders['JobTitleChange'], ColHeaders['DlrtoDlr'], ColHeaders['TotalAddedDealer'],
      ColHeaders['TotalLeavingRoleDealer'], ColHeaders['Terminated'], ColHeaders['EndPeriod']
    ],
    props:{
      isSearchVisible: false,
      isTabStretch: false,
      description: DealerDescriptions
    }
  }
}
export const TurnOverTableSchema: DataTableSchema = {
  ...structuredClone(DefaultTableSchema),
  titleSchema: {
    label: '',
    title: 'Turnover report',
    type: TITLE_TYPE['Dynamic']
  },
  isNote: true
}
const AreaTableSchema: DataTableSchema = {
  ...TurnOverTableSchema,
  columnSchema: TurnOverConfigs[TurnOverTabIds['area']]['columnSchema'],
  exportConfig: TurnOverConfigs[TurnOverTabIds['area']]['exportSchema']
}
const DistrictTableSchema: DataTableSchema = {
  ...TurnOverTableSchema,
  columnSchema: TurnOverConfigs[TurnOverTabIds['district']]['columnSchema'],
  exportConfig: TurnOverConfigs[TurnOverTabIds['district']]['exportSchema']
}
const DealerTableSchema: DataTableSchema = {
  ...TurnOverTableSchema,
  columnSchema: TurnOverConfigs[TurnOverTabIds['dealer']]['columnSchema'],
  exportConfig: TurnOverConfigs[TurnOverTabIds['dealer']]['exportSchema']
}
export const TurnoverProps: Props = {
  isSearchVisible: false,
  isTabStretch: false
}
export const SearchKeys = {
  Area: 'area',
  Certificate: 'certificate'
}
export const TurnoverReportTabConfig: Array<TabConfig> = [
  {
    label: 'AREA',
    tabId: TurnOverTabIds['area'],
    type: SECTION_TYPE.List,
    component: null,
    searchKey: SearchKeys['Area'],
    inputs: {
      DataTableSchema: structuredClone(AreaTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['area']]['props'],
      dataInput: []
    }
  },
  {
    label: 'AREA CERTIFICATION',
    tabId: TurnOverTabIds['areaCertification'],
    type: SECTION_TYPE.List,
    searchKey: SearchKeys['Certificate'],
    component: null,
    inputs: {
      DataTableSchema: structuredClone(AreaTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['area']]['props'],
      dataInput: []
    }
  },
  {
    label: 'NATIONAL CERTIFICATION',
    tabId: TurnOverTabIds['nationalCertification'],
    type: SECTION_TYPE.List,
    searchKey: SearchKeys['Certificate'],
    component: null,
    inputs: {
      DataTableSchema: structuredClone(AreaTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['area']]['props'],
      dataInput: []
    }
  }

]

export const DistrictTurnoverReportTabConfig: Array<TabConfig> = [
  {
    label: 'DISTRICT',
    tabId: TurnOverTabIds['district'],
    type: SECTION_TYPE.List,
    component: DynamicDataTableComponent,
    searchKey: SearchKeys['Area'],
    inputs: {
      DataTableSchema: structuredClone(DistrictTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['district']]['props'],
      dataInput: []
    }
  },
  {
    label: 'DISTRICT CERTIFICATION',
    tabId: TurnOverTabIds['districtCertification'],
    type: SECTION_TYPE.List,
    searchKey: SearchKeys['Certificate'],
    component: DynamicDataTableComponent,
    inputs: {
      DataTableSchema: structuredClone(DistrictTableSchema),
      props:TurnOverConfigs[TurnOverTabIds['district']]['props'],
      dataInput: []
    }
  }

]

export const DealerTurnoverReportTabConfig: Array<TabConfig> = [
  {
    label: 'DEALER',
    tabId: TurnOverTabIds['dealer'],
    type: SECTION_TYPE.List,
    component: DynamicDataTableComponent,
    searchKey: SearchKeys['Area'],
    inputs: {
      DataTableSchema: structuredClone(DealerTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['dealer']]['props'],
      dataInput: []
    }
  },
  {
    label: 'DEALER CERTIFICATION',
    tabId: TurnOverTabIds['dealerCertification'],
    type: SECTION_TYPE.List,
    searchKey: SearchKeys['Certificate'],
    component: DynamicDataTableComponent,
    inputs: {
      DataTableSchema: structuredClone(DealerTableSchema),
      props: TurnOverConfigs[TurnOverTabIds['dealer']]['props'],
      dataInput: []
    }
  }

]
